import { Checkbox } from '../../../../../../widgets/checkbox';
import { Dialog } from '../../../../../../widgets/dialog';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import React from 'react';
import { T } from 'src/shared/helpers';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import { ValidationBox } from '../../../../../../widgets/validation/validationBox';
import { ComposerAttributeNode } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAttributeNodeAdapter } from '../helpers/composerNodesAdapter';
import Stack from '../../../../../../widgets/stack/stack';
import { ComposerAttributeSelectionGrid, SelectionFooter, SelectionFooterPrimaryButton, SelectionHeader, toggleSetItem } from './composerSelectionLayout';

interface ComposerMergeAttributeOutput {
    newAttribute: ComposerAttributeNode;
    attributesToMerge?: ComposerAttributeNode[];
    deleteSourceAttributes: boolean;
}
interface ComposerMergeAttributeDialogProps {
    attributesList: ComposerAttributeNode[];
    onConfirm: (attributes: ComposerMergeAttributeOutput) => void;
}

const ComposerMergeAttributeDialogContent: React.FC<ComposerMergeAttributeDialogProps> = ({ attributesList, onConfirm }) => {
    const validationContext = useValidationContext();
    const [deleteSourceAttributes, setDeleteSourceAttributes] = React.useState<boolean>(false);
    const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
    const [attributeName, setAttributeName] = React.useState<string>('');
    const [attributeCode, setAttributeCode] = React.useState<string>('');

    const allSelected = selectedIds.size === attributesList.length;

    const handleConfirm = () => {
        if (!validationContext.submit()) return;

        const newParsedAttribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        newParsedAttribute.name = attributeName;
        newParsedAttribute.code = attributeCode;
        onConfirm({
            newAttribute: newParsedAttribute,
            attributesToMerge: attributesList.filter((attr) => selectedIds.has(attr.id)),
            deleteSourceAttributes
        });
    };

    return (
        <Dialog
            size='medium'
            title={T._('Select Attributes to Merge', 'Admin')}
            footerContent={
                <SelectionFooter>
                    <Checkbox label={T._('Delete the source attributes', 'Admin')} checked={deleteSourceAttributes} onChange={setDeleteSourceAttributes} />
                    <SelectionFooterPrimaryButton onClick={handleConfirm}>{T._('Merge', 'Admin')}</SelectionFooterPrimaryButton>
                </SelectionFooter>
            }
        >
            <Stack>
                <SelectionHeader>
                    <p>{T._('Please select below the attributes you want to merge into a single attribute.', 'Admin')}</p>
                    <Checkbox
                        label={T._('Select all', 'Admin')}
                        checked={selectedIds.size > 0}
                        indeterminate={selectedIds.size > 0 && !allSelected}
                        onChange={() => setSelectedIds(allSelected ? new Set() : new Set(attributesList.map((attr) => attr.id)))}
                    />
                </SelectionHeader>
                <ValidationBox
                    fullWidth
                    validationKey='composerMergeAttributes'
                    validationLabel={T._('Attributes to merge', 'Admin')}
                    validationValue={selectedIds}
                    validate={(value) => value.size < 2}
                    validationMessage={T._('Select at least two attributes to merge.', 'Admin')}
                >
                    <ComposerAttributeSelectionGrid
                        attributes={attributesList}
                        selectedIds={selectedIds}
                        onChange={(attributeID, selected) => setSelectedIds((prev) => toggleSetItem(prev, attributeID, selected))}
                    />
                </ValidationBox>
                <FormControl label={T._('Name', 'Admin')}>
                    <Input
                        type='text'
                        value={attributeName}
                        placeholder={T._('Enter attribute name', 'Admin')}
                        onChange={(e) => setAttributeName(e.target.value)}
                        validationKey='composerMergeAttributeName'
                        validationLabel={T._('Name', 'Admin')}
                        validate={validateRequiredTrimmed}
                        validationMessage={T._('New Attribute Name is required.', 'Admin')}
                    />
                </FormControl>
                <FormControl label={T._('Code', 'Admin')}>
                    <Input
                        type='text'
                        value={attributeCode}
                        placeholder={T._('Enter attribute code', 'Admin')}
                        onChange={(e) => setAttributeCode(e.target.value)}
                        validationKey='composerMergeAttributeCode'
                        validationLabel={T._('Code', 'Admin')}
                        validate={validateRequiredTrimmed}
                        validationMessage={T._('New Attribute Code is required.', 'Admin')}
                    />
                </FormControl>
            </Stack>
        </Dialog>
    );
};

export const ComposerMergeAttributeDialog: React.FC<ComposerMergeAttributeDialogProps> = (props) => (
    <ValidationStateProvider>
        <ComposerMergeAttributeDialogContent {...props} />
    </ValidationStateProvider>
);
